<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: Test runner for LogicFlow test cases — displays test cases, runs individual or bulk tests, shows inputs/expectations/results with pass/fail badges.
-->

@if (initialized) {

  <!-- Toolbar -->
  <div class="flex align-items-center justify-content-between mb-3">
    <div class="flex align-items-center gap-2">
      <span class="font-semibold text-lg">Testfälle</span>
      @if (testCases.length > 0) {
        <p-tag [value]="testCases.length + ' Testfälle'" severity="secondary" [rounded]="true"></p-tag>
      }
    </div>
    <div class="flex align-items-center gap-2">

      <!-- Run all tests button -->
      @if (testCases.length > 0) {
        <p-button
          label="Alle Tests ausführen"
          icon="fa-regular fa-play"
          severity="info"
          [outlined]="true"
          [loading]="runningAllTests"
          [disabled]="runningTestId !== null"
          (onClick)="runAllTests()">
        </p-button>
      }

      <!-- Refresh button -->
      <p-button
        icon="fa-regular fa-arrows-rotate"
        [outlined]="true"
        severity="secondary"
        pTooltip="Aktualisieren"
        tooltipPosition="top"
        [loading]="busy"
        (onClick)="onRefresh()">
      </p-button>
    </div>
  </div>

  <!-- Error state -->
  @if (errorMessage && !runningAllTests) {
    <div class="flex flex-column align-items-center justify-content-center p-5 surface-ground border-round mb-3">
      <i class="fa-regular fa-triangle-exclamation text-4xl text-red-500 mb-3"></i>
      <span class="text-color-secondary mb-3">{{ errorMessage }}</span>
      <p-button
        label="Erneut versuchen"
        icon="fa-regular fa-arrows-rotate"
        [outlined]="true"
        (onClick)="onRefresh()">
      </p-button>
    </div>
  }

  <!-- Suite Run Results -->
  @if (suiteResult) {
    <div class="mb-4 surface-card border-round border-1 surface-border p-4">
      <div class="flex align-items-center justify-content-between mb-3">
        <div class="flex align-items-center gap-3">
          <span class="font-semibold text-lg">Testergebnisse</span>
          @if (suiteResult.passed === suiteResult.totalTests) {
            <p-tag value="Alle bestanden" severity="success" icon="fa-regular fa-circle-check"></p-tag>
          } @else {
            <p-tag
              [value]="suiteResult.passed + ' von ' + suiteResult.totalTests + ' bestanden'"
              [severity]="suiteResult.passed === 0 ? 'danger' : 'warn'"
              icon="fa-regular fa-triangle-exclamation">
            </p-tag>
          }
          <p-tag styleClass="bg-yellow-100 text-yellow-800"
                 value="SANDBOX"
                 icon="fa-regular fa-flask"
                 pTooltip="Testläufe sind isoliert — keine echten Datenänderungen"
                 tooltipPosition="top"></p-tag>
        </div>
        <div class="flex align-items-center gap-3">
          <p-button
            icon="fa-regular fa-xmark"
            [rounded]="true"
            [text]="true"
            severity="secondary"
            pTooltip="Ergebnisse schließen"
            tooltipPosition="top"
            (onClick)="clearSuiteResults()">
          </p-button>
        </div>
      </div>

      <!-- Summary progress bar -->
      <p-progressBar
        [value]="getSuitePassPercentage()"
        [showValue]="false"
        styleClass="mb-3"
        style="height: 0.5rem;">
      </p-progressBar>

      <!-- Individual test results -->
      @if (suiteResultMap.size > 0) {
        <div class="flex flex-column gap-1">
          @for (tc of testCases; track tc.id) {
            @if (getSuiteResultForTestCase(tc.id); as result) {
              <div class="surface-ground border-round">
                <div class="flex align-items-center justify-content-between p-3 cursor-pointer"
                     (click)="toggleSuiteResultExpansion(tc.id)">
                  <div class="flex align-items-center gap-3">
                    @if (!result.success) {
                      <i class="fa-regular"
                         [class.fa-chevron-right]="!isSuiteResultExpanded(tc.id)"
                         [class.fa-chevron-down]="isSuiteResultExpanded(tc.id)">
                      </i>
                    }
                    @if (result.success) {
                      <p-tag value="Bestanden" severity="success" icon="fa-regular fa-check"></p-tag>
                    } @else {
                      <p-tag value="Fehlgeschlagen" severity="danger" icon="fa-regular fa-xmark"></p-tag>
                    }
                    <span class="font-medium">{{ tc.name }}</span>
                  </div>
                  <span class="text-color-secondary text-sm">{{ formatDuration(result.durationMs) }}</span>
                </div>

                <!-- Expanded failure details -->
                @if (!result.success && isSuiteResultExpanded(tc.id)) {
                  <div class="px-3 pb-3">
                    @if (result.failedExpectations && result.failedExpectations.length > 0) {
                      <div class="flex flex-column gap-2">
                        @for (fe of result.failedExpectations; track $index) {
                          <div class="flex align-items-start gap-2 p-2 border-round" style="background-color: var(--red-50);">
                            <i class="fa-regular fa-circle-xmark text-red-500 mt-1"></i>
                            <span class="text-sm text-red-700">{{ fe }}</span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      }
    </div>
  }

  <!-- Running all tests indicator -->
  @if (runningAllTests) {
    <div class="mb-4 surface-card border-round border-1 surface-border p-4">
      <div class="flex align-items-center gap-3 mb-3">
        <i class="fa-regular fa-spinner-third fa-spin text-primary"></i>
        <span class="font-semibold">Tests werden ausgeführt...</span>
      </div>
      <p-progressBar mode="indeterminate" style="height: 0.375rem;"></p-progressBar>
    </div>
  }

  <!-- Loading state -->
  @if (busy && testCases.length === 0 && !errorMessage) {
    <div class="flex flex-column gap-2">
      @for (i of [1, 2, 3]; track i) {
        <div class="surface-card border-round p-3">
          <p-skeleton width="100%" height="2.5rem"></p-skeleton>
        </div>
      }
    </div>
  }

  <!-- Empty state -->
  @if (!busy && testCases.length === 0 && !errorMessage) {
    <div class="flex flex-column align-items-center justify-content-center p-5 surface-ground border-round">
      <i class="fa-regular fa-flask-vial text-4xl text-color-secondary mb-3"></i>
      <span class="text-color-secondary">Keine Testfälle vorhanden</span>
    </div>
  }

  <!-- Test case list -->
  @if (testCases.length > 0) {
    <p-table
      [value]="testCases"
      [selectionMode]="'single'"
      [(selection)]="selectedTestCase"
      (onRowSelect)="selectTestCase($any($event.data))"
      (onRowUnselect)="selectTestCase($any($event.data))"
      [rowHover]="true"
      [scrollable]="true"
      scrollHeight="20rem"
      styleClass="p-datatable-sm">

      <ng-template pTemplate="header">
        <tr>
          <th style="width: 3rem">Aktiv</th>
          <th>Name</th>
          <th style="width: 12rem">Letzter Lauf</th>
          <th style="width: 8rem">Letztes Ergebnis</th>
          <th style="width: 8rem">Dauer</th>
          <th style="width: 10rem">Aktionen</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-testCase>
        <tr [pSelectableRow]="testCase"
            [class.surface-50]="selectedTestCase?.id === testCase.id">
          <td>
            @if (testCase.active) {
              <i class="fa-regular fa-circle-check text-green-500"></i>
            } @else {
              <i class="fa-regular fa-circle-xmark text-color-secondary"></i>
            }
          </td>
          <td>
            <div class="flex align-items-center gap-2">
              <span class="font-medium">{{ testCase.name }}</span>
              @if (testCase.mockDataJson && testCase.mockDataJson.length > 2) {
                <p-tag styleClass="bg-pink-50 text-pink-700"
                       value="CTX-MOCK"
                       icon="fa-regular fa-mask"
                       pTooltip="Dieser Testfall mockt $ctx-Operationen: findById, findBy, executeLogic, executeSubFlow, lookupMapping, searchEntity."
                       tooltipPosition="top"></p-tag>
              }
            </div>
            @if (testCase.description) {
              <div class="text-color-secondary text-xs mt-1">{{ testCase.description }}</div>
            }
          </td>
          <td>
            @if (testCase.lastRunAt) {
              <span class="text-sm">{{ testCase.lastRunAt | date:'dd.MM.yyyy HH:mm:ss' }}</span>
            } @else {
              <span class="text-color-secondary text-sm">Noch nicht ausgeführt</span>
            }
          </td>
          <td>
            @if (testCase.lastRunSuccess === true) {
              <p-tag value="Bestanden" severity="success" icon="fa-regular fa-check" [rounded]="true"></p-tag>
            } @else if (testCase.lastRunSuccess === false) {
              <p-tag value="Fehlgeschlagen" severity="danger" icon="fa-regular fa-xmark" [rounded]="true"></p-tag>
            } @else {
              <span class="text-color-secondary text-sm">-</span>
            }
          </td>
          <td>
            <span class="text-sm">{{ formatDuration(testCase.lastRunDurationMs) }}</span>
          </td>
          <td>
            <p-button
              label="Ausführen"
              icon="fa-regular fa-play"
              [outlined]="true"
              size="small"
              [loading]="runningTestId === testCase.id"
              [disabled]="runningAllTests || (runningTestId !== null && runningTestId !== testCase.id)"
              (onClick)="runTestCase(testCase, $event)">
            </p-button>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center text-color-secondary p-4">
            Keine Testfälle gefunden
          </td>
        </tr>
      </ng-template>
    </p-table>
  }

  <!-- Test Case Detail Panel -->
  @if (selectedTestCase) {
    <div class="mt-4 grid">

      <!-- Inputs Section -->
      <div class="col-12 lg:col-6">
        <div class="surface-card border-round border-1 surface-border p-3 h-full">
          <div class="flex align-items-center justify-content-between mb-3">
            <span class="font-semibold">Eingaben</span>
            @if (loadingInputs) {
              <i class="fa-regular fa-spinner-third fa-spin text-color-secondary"></i>
            }
          </div>

          @if (loadingInputs && testCaseInputs.length === 0) {
            <p-skeleton width="100%" height="6rem"></p-skeleton>
          }

          @if (!loadingInputs && testCaseInputs.length === 0) {
            <div class="flex align-items-center justify-content-center p-3 surface-ground border-round">
              <span class="text-color-secondary text-sm">Keine Eingaben definiert</span>
            </div>
          }

          @if (testCaseInputs.length > 0) {
            <p-table
              [value]="testCaseInputs"
              styleClass="p-datatable-sm"
              [scrollable]="true"
              scrollHeight="12rem">

              <ng-template pTemplate="header">
                <tr>
                  <th>Variablenname</th>
                  <th>Wert</th>
                  <th>Typ</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-input>
                <tr>
                  <td>
                    <span class="text-sm font-medium">{{ input.variableName }}</span>
                  </td>
                  <td>
                    @if (input.variableValue) {
                      <span class="text-sm">{{ input.variableValue }}</span>
                    } @else {
                      <span class="text-color-secondary text-sm">null</span>
                    }
                  </td>
                  <td>
                    @if (input.variableType) {
                      <p-tag [value]="input.variableType" severity="secondary" [rounded]="true"></p-tag>
                    } @else {
                      <span class="text-color-secondary text-sm">-</span>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </div>
      </div>

      <!-- Expectations Section -->
      <div class="col-12 lg:col-6">
        <div class="surface-card border-round border-1 surface-border p-3 h-full">
          <div class="flex align-items-center justify-content-between mb-3">
            <span class="font-semibold">Erwartungen</span>
            @if (loadingExpectations) {
              <i class="fa-regular fa-spinner-third fa-spin text-color-secondary"></i>
            }
          </div>

          @if (loadingExpectations && testCaseExpectations.length === 0) {
            <p-skeleton width="100%" height="6rem"></p-skeleton>
          }

          @if (!loadingExpectations && testCaseExpectations.length === 0) {
            <div class="flex align-items-center justify-content-center p-3 surface-ground border-round">
              <span class="text-color-secondary text-sm">Keine Erwartungen definiert</span>
            </div>
          }

          @if (testCaseExpectations.length > 0) {
            <p-table
              [value]="testCaseExpectations"
              styleClass="p-datatable-sm"
              [scrollable]="true"
              scrollHeight="12rem">

              <ng-template pTemplate="header">
                <tr>
                  <th>Typ</th>
                  <th>Zielausdruck</th>
                  <th>Erwarteter Wert</th>
                  <th>Vergleich</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-expectation>
                <tr>
                  <td>
                    <p-tag
                      [value]="getExpectationTypeLabel(expectation.expectationType)"
                      severity="secondary"
                      [rounded]="true">
                    </p-tag>
                  </td>
                  <td>
                    @if (expectation.targetExpression) {
                      <span class="text-sm font-mono">{{ expectation.targetExpression }}</span>
                    } @else {
                      <span class="text-color-secondary text-sm">-</span>
                    }
                  </td>
                  <td>
                    @if (expectation.expectedValue) {
                      <span class="text-sm">{{ expectation.expectedValue }}</span>
                    } @else {
                      <span class="text-color-secondary text-sm">-</span>
                    }
                  </td>
                  <td>
                    @if (expectation.comparisonType !== null && expectation.comparisonType !== undefined) {
                      <span class="text-sm">{{ getComparisonTypeLabel(expectation.comparisonType) }}</span>
                    } @else {
                      <span class="text-color-secondary text-sm">-</span>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </div>
      </div>
    </div>
  }

  <!-- Single Test Result Display -->
  @if (testResult) {
    <div class="mt-4 surface-card border-round border-1 surface-border p-4">
      <div class="flex align-items-center justify-content-between mb-3">
        <div class="flex align-items-center gap-3">
          <span class="font-semibold text-lg">Testergebnis</span>
          @if (testResult.success) {
            <p-tag value="Bestanden" severity="success" icon="fa-regular fa-circle-check"></p-tag>
          } @else {
            <p-tag value="Fehlgeschlagen" severity="danger" icon="fa-regular fa-circle-xmark"></p-tag>
          }
          <p-tag styleClass="bg-yellow-100 text-yellow-800"
                 value="SANDBOX"
                 icon="fa-regular fa-flask"
                 pTooltip="Testläufe sind isoliert — keine echten Datenänderungen"
                 tooltipPosition="top"></p-tag>
        </div>
        <div class="flex align-items-center gap-3">
          <span class="text-color-secondary text-sm">{{ formatDuration(testResult.durationMs) }}</span>
          @if (testResult.executionId) {
            <p-button
              label="Ausführung anzeigen"
              icon="fa-regular fa-arrow-up-right-from-square"
              [outlined]="true"
              size="small"
              severity="secondary"
              pTooltip="Zur Ausführungsansicht wechseln"
              tooltipPosition="top">
            </p-button>
          }
        </div>
      </div>

      <!-- Failed expectations -->
      @if (testResult.failedExpectations && testResult.failedExpectations.length > 0) {
        <div class="mb-3">
          <span class="font-semibold text-sm mb-2 block">Fehlgeschlagene Erwartungen</span>
          <div class="flex flex-column gap-2">
            @for (fe of testResult.failedExpectations; track $index) {
              <div class="flex align-items-start gap-2 p-3 border-round" style="background-color: var(--red-50);">
                <i class="fa-regular fa-circle-xmark text-red-500 mt-1"></i>
                <span class="text-sm text-red-700">{{ fe }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Messages during test -->
      @if (testResult.messages && testResult.messages.length > 0) {
        <div>
          <span class="font-semibold text-sm mb-2 block">Nachrichten</span>
          <div class="flex flex-column gap-1">
            @for (msg of testResult.messages; track $index) {
              <div class="flex align-items-center gap-2 p-2 surface-ground border-round">
                <span class="text-sm">{{ msg }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }

} @else {
  <!-- Pre-initialization loading -->
  <div class="flex flex-column gap-3 p-3">
    <p-skeleton width="100%" height="2.5rem"></p-skeleton>
    <p-skeleton width="100%" height="12rem"></p-skeleton>
  </div>
}
